import React, { useState, useEffect } from 'react';
import { Box, Paper, Stack, Typography, InputBase, Button, Divider, List, ListItem, ListItemButton, ListItemText, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search } from '@mui/icons-material';
import { motion } from 'framer-motion';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchTrackingsByNumbers, selectTracking } from '../../store/slices/trackingSlice';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const TrackingPage = () => {
    const query = useQuery();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { trackings, loading, error } = useAppSelector(selectTracking);

    const [input, setInput] = useState('');
    const [selectedIdx, setSelectedIdx] = useState(0);

    const numbersParam = query.get('numbers') || '';

    const theme = useTheme();
    const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
    const showSidebar = trackings.length > 1 && !isSmDown;
    const showTopList = trackings.length > 1 && isSmDown;

    useEffect(() => {
        const numbers = numbersParam.split(',').map(s => s.trim()).filter(Boolean);
        setInput(numbers.join(', '));
        if (numbers.length > 0) {
            dispatch(fetchTrackingsByNumbers(numbers));
        }
        setSelectedIdx(0);
    }, [numbersParam, dispatch]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInput(e.target.value);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const numbers = input.split(/[,\s]+/).filter(Boolean);
        if (numbers.length === 0) return;
        navigate(`/tracking?numbers=${encodeURIComponent(numbers.join(','))}`);
    };

    const renderContent = () => {
        if (loading === 'pending') {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                    <CircularProgress color="secondary" />
                    <Typography color="grey.400" sx={{ textAlign: 'center', fontSize: 20, mt: 2 }}>Fetching details...</Typography>
                </Box>
            );
        }

        if (error) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
                    <Typography color="error" sx={{ textAlign: 'center', fontSize: 20 }}>{error}</Typography>
                </Box>
            );
        }

        if (trackings.length === 0) {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'start', height: '100%' }}>
                    <LocalShippingIcon sx={{ fontSize: 106, color: 'secondary.main', opacity: 0.5 }} />
                    <Typography color="grey.400" sx={{ textAlign: 'center', fontSize: 20 }}>Enter a consignment number to track.</Typography>
                </Box>
            );
        }

        if (!trackings[selectedIdx]) {
            return <Typography>Something went wrong.</Typography>; // Should not happen if logic is correct
        }

        return (
            <>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: 'white', textAlign: 'center' }}>
                    Shipment Details
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', md: 'flex-start' },
                    gap: 2,
                    px: { xs: 1, md: 2 },
                    mb: 3
                }}>
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: 'repeat(1, 1fr)',
                            sm: 'repeat(2, 1fr)',
                            md: 'repeat(5, 1fr)',
                        },
                        gap: { xs: 3, md: 2 },
                        flexGrow: 1
                    }}>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Consignment No.</Typography>
                            <Typography sx={{ color: 'white', fontWeight: 600 }}>{trackings[selectedIdx].number}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Booking Date</Typography>
                            <Typography sx={{ color: 'white', fontWeight: 600 }}>{trackings[selectedIdx].bookingDate}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>Booking Type</Typography>
                            <Typography sx={{ color: 'white', fontWeight: 600 }}>{trackings[selectedIdx].bookingType}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>From</Typography>
                            <Typography sx={{ color: 'white', fontWeight: 600 }}>{trackings[selectedIdx].fromInfo.name}</Typography>
                            <Typography sx={{ color: 'grey.400', fontSize: '0.8rem' }}>{trackings[selectedIdx].fromInfo.city}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>To</Typography>
                            <Typography sx={{ color: 'white', fontWeight: 600 }}>{trackings[selectedIdx].toInfo.name}</Typography>
                            <Typography sx={{ color: 'grey.400', fontSize: '0.8rem' }}>{trackings[selectedIdx].toInfo.city}</Typography>
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.08)' }} />

                <Stack spacing={0} sx={{ position: 'relative', mt: 3 }}>
                    {trackings[selectedIdx].statuses.map((s, i, arr) => {
                        const isLatest = i === 0;
                        const isLast = i === arr.length - 1;
                        const isFirst = i === 0;
                        return (
                            <Box key={i} sx={{ width: '100%' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        position: 'relative',
                                        minHeight: 100,
                                    }}
                                >
                                    {/* Left: Date/Time/Location */}
                                    <Box sx={{ width: 180, textAlign: 'right', pr: 3 }}>
                                        <Typography sx={{ fontWeight: 500, color: '#aaa', fontSize: 16 }}>
                                            {s.date}
                                        </Typography>
                                        {s.time && (
                                            <Typography sx={{ color: '#aaa', fontSize: 15 }}>
                                                {s.time}
                                            </Typography>
                                        )}
                                        {s.location && (
                                            <Typography sx={{ color: '#aaa', fontSize: 15, fontWeight: 600 }}>
                                                {s.location}
                                            </Typography>
                                        )}
                                    </Box>
                                    {/* Center: Dot + Line (simple flex column, no absolute) */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 24, width: 24, height: 100, justifyContent: 'center' }}>
                                        {/* Line above (only if not first) */}
                                        <Box sx={{ flex: 0.5, display: 'flex', alignItems: 'center' }}>
                                            {!isFirst && <Box sx={{ width: 2, height: '100%', bgcolor: '#bbb', zIndex: 1 }} />}
                                        </Box>
                                        {/* Dot */}
                                        <Box
                                            sx={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: '50%',
                                                bgcolor: isLatest ? 'secondary.main' : '#bbb',
                                                border: isLatest ? '2px solid #bc2222' : '2px solid #bbb',
                                                zIndex: 2,
                                                boxSizing: 'border-box',
                                            }}
                                        />
                                        {/* Line below (only if not last) */}
                                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                            {!isLast && <Box sx={{ width: 2, height: '100%', bgcolor: '#bbb', zIndex: 1 }} />}
                                        </Box>
                                    </Box>
                                    {/* Right: Status/Description */}
                                    <Box sx={{ pl: 3, flex: 1 }}>
                                        <Typography sx={{
                                            fontSize: isLatest ? 18 : 16,
                                            fontWeight: isLatest ? 700 : 600,
                                            color: isLatest ? 'secondary.main' : 'white',
                                        }}>
                                            {s.status}
                                        </Typography>
                                        {s.desc && (
                                            <Typography sx={{ color: '#888', fontSize: 15, mt: 0.5 }}>
                                                {s.desc}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Stack>
            </>
        );
    };

    return (
        <Box sx={{ minHeight: '100vh', color: 'white', py: 0 }}>
            {/* Landing Input */}
            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'end', alignItems: 'center', minHeight: '200px', height: '30vh' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: 'secondary.main', textAlign: 'center' }}>
                    Consignment Tracking
                </Typography>
                <Paper
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: { xs: '95%', sm: 480 },
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: 0,
                        mt: 2,
                        bgcolor: 'background.paper',
                        border: '2px solid transparent',
                        borderColor: '#777',
                        '&:focus-within': {
                            border: '2px solid',
                            borderColor: 'secondary.main',
                            boxShadow: '0 0 0 4px rgba(188,34,34,0.25)',
                        },
                    }}
                    elevation={0}
                >
                    <InputBase
                        value={input}
                        onChange={handleInputChange}
                        sx={{ ml: 2, flex: 1, fontSize: 18 }}
                        placeholder="Enter Consignment Number(s)"
                        inputProps={{ 'aria-label': 'Enter Consignment Number(s)' }}
                    />
                    <Button
                        type="submit"
                        sx={{
                            bgcolor: 'secondary.main',
                            color: 'white',
                            minWidth: 56,
                            height: 56,
                            borderRadius: 0,
                            '&:hover': { bgcolor: 'secondary.dark' },
                        }}
                    >
                        <Search sx={{ fontSize: 32 }} />
                    </Button>
                </Paper>
            </Box>

            <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.08)' }} />

            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.4, 0.1, 0.2, 1] }}
            >
                <Paper
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: isSmDown ? 'column' : 'row',
                        minHeight: 400,
                        borderRadius: 4,
                        overflow: 'hidden',
                        mx: { xs: 1, sm: 2, md: 10 },
                        p: isSmDown ? 1 : 0,
                    }}
                >
                    {/* Top List for small screens */}
                    {showTopList && (
                        <Box sx={{
                            width: '100%',
                            display: 'flex',
                            overflowX: 'auto',
                            pb: 1,
                            mb: 2,
                            gap: 1,
                        }}>
                            {trackings.map((t, idx) => (
                                <ListItem key={t.number} disablePadding sx={{ width: 'auto', minWidth: 120 }}>
                                    <ListItemButton
                                        selected={selectedIdx === idx}
                                        onClick={() => setSelectedIdx(idx)}
                                        sx={{
                                            borderRadius: 2,
                                            mx: 1,
                                            my: 0.5,
                                            minWidth: 100,
                                            bgcolor: selectedIdx === idx ? 'secondary.main' : '#28292b',
                                            color: 'white',
                                            '&:hover': {
                                                bgcolor: 'secondary.main',
                                                color: 'white',
                                            },
                                        }}
                                    >
                                        <ListItemText
                                            primary={t.number}
                                            primaryTypographyProps={{ fontWeight: 600, color: 'white', fontSize: 16 }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </Box>
                    )}
                    {/* Sidebar for desktop/tablet if more than 1 consignment */}
                    {showSidebar && (
                        <Box sx={{ width: { xs: 120, sm: 220 }, bgcolor: '#28292b', borderRight: '1px solid #333', pt: 2, px: 1 }}>
                            <List>
                                {trackings.map((t, idx) => (
                                    <ListItem key={t.number} disablePadding>
                                        <ListItemButton
                                            selected={selectedIdx === idx}
                                            onClick={() => setSelectedIdx(idx)}
                                            sx={{
                                                borderRadius: 2,
                                                mx: 1,
                                                my: 0.5,
                                                '&.Mui-selected': {
                                                    bgcolor: 'secondary.main',
                                                    '&:hover': {
                                                        bgcolor: 'secondary.main',
                                                        color: 'white',
                                                    },
                                                },
                                            }}
                                        >
                                            <ListItemText
                                                primary={t.number}
                                                primaryTypographyProps={{ fontWeight: 600, color: 'white', fontSize: 16 }}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                    {/* Timeline/Details section */}
                    <Box
                        sx={{
                            flex: 1,
                            p: { xs: 1, sm: 3, md: 4 },
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'start',
                            minWidth: 0,
                        }}
                    >
                        {renderContent()}
                    </Box>
                </Paper>
            </motion.div>
        </Box>
    );
};

export default TrackingPage; 